import React, { useState } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import SectionBasePage from 'components/SectionBasePage';
import { updateUserAction, usersEntitiesSelector } from 'state/slices/users';
import { useParams } from 'react-router';
import { Button } from 'UI';
import { useBreadcrumbNavigation } from 'hooks/breadcrumb';
import { Form } from 'antd';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { userProfileSelector } from 'state/slices/session';

import UpdateForm, { UpdateUserFormData } from './components/UpdateForm';
import {
  MainContainer,
} from './style';

interface UserDetailsRouteParams {
  userId: string
}

interface DetailsPageProps {
  onUpdateFinish?(): void
}

const UserDetailPage = ({
  onUpdateFinish,
}: DetailsPageProps): JSX.Element => {
  const currentUser = useSelector(userProfileSelector);
  const { userId } = useParams<UserDetailsRouteParams>();
  const usersEntites = useSelector(usersEntitiesSelector);
  const user = usersEntites[userId];

  const [form] = Form.useForm<UpdateUserFormData>();
  const [isLoading, setLoading] = useState(false);
  const [isUpdated, setUpdated] = useState(false);
  const navigator = useBreadcrumbNavigation();
  const dispatch = useDispatch();

  const onSaveClick = () => {
    form.submit();
  };

  const onFormSubmit = async (data: UpdateUserFormData) => {
    setLoading(true);
    try {
      setLoading(true);
      await dispatch(updateUserAction({
        id: userId,
        data,
      }));
      const successAlert = createAlertNotification(`
        Usuario atualizado com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      if (onUpdateFinish) onUpdateFinish();
      navigator.toPrevious();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar criar usuario. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  const onFormChange = (_: unknown, data: UpdateUserFormData) => {
    const partialUser = {
      name: user?.name,
      email: user?.email,
      isAdmin: user?.isAdmin,
      isActive: user?.isActive,
    };
    if (JSON.stringify(data) === JSON.stringify(partialUser)) {
      setUpdated(false);
    } else {
      setUpdated(true);
    }
  };

  return (
    <SectionBasePage
      title={user?.name || 'Detalhes'}
      extra={(
        <Button
          loading={isLoading}
          onClick={onSaveClick}
          disabled={!isUpdated}
        >
          Salvar edições
        </Button>
      )}
    >
      {user && (
        <MainContainer>
          <UpdateForm
            data={user}
            onDataChange={onFormChange}
            formHandler={form}
            onSubmit={onFormSubmit}
            isLoading={isLoading}
            isCurrentUser={currentUser?.id === userId}
          />
        </MainContainer>
      )}
    </SectionBasePage>
  );
};

const defaultProps: DetailsPageProps = {
  onUpdateFinish: () => {},
};

UserDetailPage.defaultProps = defaultProps;

export default UserDetailPage;
